import { GetPointResponse, GetTopicResponse } from '../types';
import { isDefined, isValidString } from './utils';

import { DUMMY_DATE } from './constants';
import format from 'date-fns/format';
import { timezoneAsync } from './api/google-api';

export function toDateOrUndefined(value: Date | string | undefined | null) {
    if (value === undefined || value === null || value === '') return undefined;

    return new Date(value);
}

export function convertUTCToLocale(dateString: string, timeZone: string) {
    const date = new Date(dateString);
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
    return tzDate;
}

export function convertUTCtoTimeZone(dateString: string, rawOffset: number) {
    const date = new Date(dateString);
    const localTimeOffset = date.getTimezoneOffset() * 60 * 1000; // Convert to milliseconds
    const tzDate = new Date(date.getTime() + rawOffset - localTimeOffset);
    return tzDate;
}
export function convertTZ(date: Date, tzString: string) {
    return new Date((typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {timeZone: tzString}));
}



export function convertLocaleToUTC(dateString: string | undefined, timeZone: string) {
    if (dateString === undefined) {
        return undefined;
    }
    const date = new Date(dateString);
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
    const offset = utcDate.getTime() - tzDate.getTime();
    date.setTime(date.getTime() + offset);
    return date;
}

export const validateDate = (date: string) => {
    try {
        new Date(date).toISOString();
        return true;
    } catch {
        return false;
    }
};

export const filterPointByRange = (point: any, startString: string | undefined, endString: string | undefined) => {
    // if point start and point end are undefined, return true
    if (!isDefined(point.start) && !isDefined(point.end) && !isDefined(endString)){
        return true;
    }

    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

    // If point.start or point.end is the DUMMY_DATE, replace the date part with today's date but keep the time
    const pointStart = isDefined(point.start) && point.start.includes(DUMMY_DATE) === true ? today + (point.start.slice(10) as string) : point.start;
    let pointEnd = point.end;

    if (!isDefined(pointEnd)) {
        if (isDefined(pointStart) && pointStart.includes(DUMMY_DATE) === true) {
            // Make point.end one hour greater than point.start (this is for time-only topics)
            const startDate = new Date(pointStart);
            const endDate = new Date(startDate.getTime());
            endDate.setHours(endDate.getHours() + 1);
            pointEnd = endDate.toISOString();
        } else if(isDefined(pointStart)) {
            // Make point.end one day later than point.start
            const startDate = new Date(pointStart);
            const endDate = new Date(startDate.getTime());
            endDate.setDate(endDate.getDate() + 1);
            pointEnd = endDate.toISOString();
        }
    } else if (isDefined(pointEnd) && pointEnd.includes(DUMMY_DATE) === true) {
        pointEnd = today + (pointEnd.slice(10) as string);
    }
    // otherwise, point.end is already in the correct format

    const startLimit =
        endString === undefined ||
        new Date(pointStart).getTime() <= new Date(endString).getTime();
    const endLimit =
        startString === undefined ||
        new Date(pointEnd).getTime() >= new Date(startString).getTime();

    return startLimit && endLimit;
};

// get relative time from iso string
export function getRelativeTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(weeks / 4.34524);
    const years = Math.floor(months / 12);

    if (seconds < 60) {
        return `a few seconds ago`;
    } else if (minutes < 60) {
        return `${minutes} minutes ago`;
    } else if (hours < 24) {
        return `${hours} hours ago`;
    } else if (days < 7) {
        return `${days} days ago`;
    } else if (weeks < 4.34524) {
        return `${weeks} weeks ago`;
    } else if (months < 12) {
        return `${months} months ago`;
    } else {
        return `${years} years ago`;
    }
}

export async function getDateTimeStringForPoint(point: GetPointResponse, topic: GetTopicResponse): Promise<string | undefined> {
    if (point?.start && topic.has_dates) {
    // Get Timezone info for the point
    const originTimezoneInfo = await timezoneAsync(
        point.location.latLng.lat,
        point.location.latLng.lng
    );
    const timezoneStringOrigin = originTimezoneInfo[0].shortIdentifier as string;

    // convert point.start and end to point timezone
    const adjustedStartDate = convertUTCToLocale(point.start ?? '', originTimezoneInfo[0].identifier);
    const adjustedEndDate = convertUTCToLocale(point.end ?? '', originTimezoneInfo[0].identifier);

    // format the date and time
    const startDateString = point.start ? format(adjustedStartDate, 'MMM dd, yyyy') : undefined;
    const startTimeString = point.start ? `${format(adjustedStartDate, 'hh:mm a')}` : '';

    const endDateString = point.end ? format(adjustedEndDate, 'MMM dd, yyyy') : undefined;
    const endTimeString = point.end ? `${format(adjustedEndDate, 'hh:mm a')}` : '';

    const isAllDay = startTimeString === '12:00 AM' && (endTimeString === '11:59 PM' || endTimeString === '' || endTimeString === undefined);
    const isDateOnly = topic?.has_date_only ?? false;
    const isTimeOnly = topic?.has_time_only ?? false;
    const isEndTimeOff = !(topic?.has_end_date_time ?? true);

    // check if start and end date are the same
    const isSameDate = startDateString === endDateString && endDateString !== undefined;
    const isSameDateAndTime = isSameDate && startTimeString === endTimeString && endTimeString !== undefined;

    if (isValidString(startDateString)) {
        if (isAllDay) {
            return `${startDateString}` + (isSameDate || !endDateString ? '' : ` - ${endDateString ?? ''}`);
        } else if (isDateOnly) {
            return `${startDateString}` + (isSameDate || !endDateString ? '' : ` - ${endDateString ?? ''}`);
        } else if (isTimeOnly) {
            return `${startTimeString}` + (isSameDateAndTime || !endDateString ? '' : ` - ${endTimeString ?? ''}`) + ` (${timezoneStringOrigin})`;
        } else if (isEndTimeOff || !point.end) {
            return `${startDateString} ${startTimeString} (${timezoneStringOrigin})`;
        } else {
            return `${startDateString} ${startTimeString}` +
                (endDateString && !isSameDateAndTime ? ` - ${endDateString}` : '') +
                (endTimeString && !isSameDateAndTime ? ` ${endTimeString}` : '') +
                ` (${timezoneStringOrigin})`;
        }
    } else {
        return undefined;
    }
}
return undefined;
}